import clsx from 'clsx';
import { useMemo } from 'react';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { useUploadFile } from '@/context/UploadFileProvider';
import type { FinalizeUploadRequest } from '@/context/UploadFileProvider/types';

interface UploadActionsProps {
  isOpen: boolean;
  isDisabled: boolean;
  onUploadToServer: FinalizeUploadRequest;
}

export function UploadActions({ isOpen, isDisabled, onUploadToServer }: UploadActionsProps) {
  const { uploads, onUploadFiles, onCancelAllUploads, onCloseUploadManager } = useUploadFile();
  const isUploading = useMemo(() => uploads.some((upload) => upload.status === 'uploading'), [uploads]);
  const isSuccess = useMemo(() => uploads.every((upload) => upload.status === 'success'), [uploads]);

  const onStartUploadToServer = () => {
    onUploadFiles(onUploadToServer);
  };

  if (isSuccess) {
    return (
      <Wrapper isOpen={isOpen} className="items-center justify-center max-w-[186px]">
        <BaseButton intent="secondary" size="full" onClick={onCloseUploadManager}>
          Close
        </BaseButton>
      </Wrapper>
    );
  }

  if (isUploading) {
    return (
      <Wrapper isOpen={isOpen} className="items-center justify-center max-w-[186px]">
        <BaseButton intent="secondary" size="full" onClick={onCancelAllUploads}>
          Cancel All Uploads
        </BaseButton>
      </Wrapper>
    );
  }

  return (
    <Wrapper isOpen={isOpen}>
      <BaseButton intent="secondary" size="full" onClick={onCloseUploadManager} className="w-1/2">
        Cancel
      </BaseButton>
      <BaseButton size="full" onClick={onStartUploadToServer} className="w-1/2" disabled={isDisabled}>
        Upload Files
      </BaseButton>
    </Wrapper>
  );
}

const Wrapper = ({
  children,
  isOpen,
  className,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  className?: string;
}) => {
  return (
    <div
      className={clsx(
        className,
        `flex gap-[20px] w-full transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100 max-h-[100px] mt-[30px]' : 'opacity-0 max-h-0 overflow-hidden'}`
      )}
    >
      {children}
    </div>
  );
};